import { jsx as _jsx } from "preact/jsx-runtime";
/* eslint-disable @typescript-eslint/no-unused-vars */
import { createContext, useMemo } from 'react';
import { useCallback, useContext, useEffect, useState, } from 'preact/compat';
import { useFormContext } from 'react-hook-form';
import { useTimeConflict } from '@bookingForm/context/TimeConflicts';
import convertSessionToDate from '@widgets/shared/convertSessionToDate';
import { addMinutes } from 'date-fns/fp';
const SessionDataContext = createContext({
    activitiesWithInputs: [],
    updateActivity: (activityId, session) => [],
    updateActivityField: (activityId, value = null) => null,
});
export function useSessionData() {
    const value = useContext(SessionDataContext);
    return value ?? {};
}
export function SessionDataProvider(props) {
    const { children, fieldPrefix, fieldName, activities, activityFieldName, } = props;
    const { addSelectedTimePeriod, selectedTimePeriods, checkTimeConflicts, overwriteTimePeriods, } = useTimeConflict();
    const [activitiesWithInputs, setActivitiesWithInputs] = useState([]);
    const { getValues, setValue } = useFormContext();
    const fieldSelector = useCallback((idx) => `${fieldPrefix}.${idx}.${fieldName}`, [fieldName, fieldPrefix]);
    const getFieldIndex = useCallback((activityId) => {
        if (!activityFieldName) {
            return activities.findIndex((theActivity) => theActivity.activity.id === activityId);
        }
        return getValues(fieldPrefix).findIndex((session) => session[activityFieldName] === activityId);
    }, [activities, activityFieldName, getValues]);
    useEffect(() => {
        setActivitiesWithInputs(activities.map((activity, idx) => {
            const theFieldSelector = fieldSelector(getFieldIndex(activity.activity.id));
            console.log('SessionData.tsx - field selector: ', theFieldSelector);
            return {
                activity,
                fieldSelector: theFieldSelector,
                fieldValue: getValues(theFieldSelector),
            };
        }));
    }, [activities, getValues]);
    const updateActivityField = useCallback((activityId, value = null) => {
        const foundActivityIdx = getFieldIndex(activityId);
        if (foundActivityIdx !== -1) {
            setValue(fieldSelector(foundActivityIdx), value);
        }
    }, [activitiesWithInputs, getValues, fieldPrefix, activityFieldName, activitiesWithInputs]);
    // reset all activities in a given array that exist within the original provided array
    const resetActivities = useCallback((activitiesToReset) => {
        activitiesToReset.forEach((activityToReset) => {
            updateActivityField(activityToReset.activity.id, null);
        });
    }, [activities]);
    const updateActivity = useCallback((activityId, session) => {
        const foundActivity = activitiesWithInputs.find((activity) => activity.activity.activity.id === activityId);
        if (foundActivity) {
            const { activity: activitySessions } = foundActivity;
            const sessionDate = convertSessionToDate(session);
            console.log('SessionData - Old selected time periods: ', selectedTimePeriods);
            const newSelectedTimePeriods = overwriteTimePeriods({
                activity: activitySessions,
                from: sessionDate,
                to: addMinutes(activitySessions.activity.durationInMinutes, sessionDate),
            });
            console.log('SessionData - New selected time periods: ', newSelectedTimePeriods);
            // remove all the activities overwritten by the new time
            const inactiveActivities = activities.map((theActivity) => {
                const foundSession = !newSelectedTimePeriods.find((theSession) => theSession.activity.activity.id === theActivity.activity.id);
                return foundSession ? {
                    ...theActivity,
                    activeSession: undefined,
                } : undefined;
            }).filter((maybeActivity) => maybeActivity !== undefined);
            resetActivities(inactiveActivities);
            console.log('SessionData - inactive activities: ', inactiveActivities);
            // update the activites with the new time selected.
            const activeActivities = activities.map((theActivity) => {
                let foundSession;
                // if this is the selected activity forcing to be the session from the params as the overwrite won't be updated here.
                if (theActivity.activity.id === activityId) {
                    foundSession = theActivity.sessions.find((theSession) => theSession.SessionId === session.SessionId);
                }
                else {
                    const foundData = newSelectedTimePeriods.find((theSession) => theActivity.activity.id === theSession.activity.activity.id);
                    foundSession = foundData ? foundData.activity.activeSession : undefined;
                }
                return foundSession ? {
                    ...theActivity,
                    activeSession: foundSession,
                } : undefined;
            }).filter((maybeActivity) => maybeActivity !== undefined);
            console.log('SessionData - active activities: ', activeActivities);
            activeActivities.forEach((activeActivity) => {
                // determing if this is the selected activity then either setting the selected time or the updated one from the time conflicts.
                updateActivityField(activeActivity.activity.id, activeActivity.activity.id === activityId
                    ? session.SessionId
                    : activeActivity.activeSession?.SessionId ?? null);
            });
            // updateActivityField(activityId, session.SessionId);
            // needs to maintain the same order as before...
            const unorderedActivities = [
                ...inactiveActivities,
                ...activeActivities,
            ];
            // doing this as merging the arrays above potentially unsynchs the order
            return activities.map((orderedActivity) => {
                const unorderedActivity = unorderedActivities.find((theActivity) => theActivity.activity.id === orderedActivity.activity.id);
                if (unorderedActivity) {
                    return {
                        ...orderedActivity,
                        activeSession: unorderedActivity.activeSession,
                    };
                }
                // should never occur but adding as a failsafe.
                return orderedActivity;
            });
        }
        return activities;
    }, [activities, overwriteTimePeriods, convertSessionToDate, activitiesWithInputs, selectedTimePeriods]);
    const value = useMemo(() => ({
        updateActivity,
        activitiesWithInputs,
        updateActivityField,
    }), [
        updateActivity,
        activitiesWithInputs,
        updateActivityField,
    ]);
    return (_jsx(SessionDataContext.Provider, { value: value, children: children }));
}
SessionDataProvider.defaultProps = {
    activityFieldName: undefined,
};
